// @givmo/design-system — Field (SaaS layer input).
//
// The fintech-grade labeled text input for the rebuilt admin/super-admin portals — distinct from
// the legacy Flutter-ported `TextField` (which the donate app + the existing auth screens still
// use). SaaS tokens: 8px radius, `neutral-500` idle border, `shadow-ring` brand focus, body
// text at s14, `text-placeholder` hint, label above (s13 / w500 / text-secondary) and an inline
// helper/error below. Optional leading/trailing adornment slots.
import { forwardRef, useId } from 'react';
import type { InputHTMLAttributes, ReactNode } from 'react';
import { cn } from '../lib/utils';
import { testIdProp, type TestIdProps } from '../lib/testid';

export interface FieldProps
  extends Omit<InputHTMLAttributes<HTMLInputElement>, 'size'>, TestIdProps {
  /** Label above the input. */
  label?: ReactNode;
  /** Helper text below the input (hidden when an error message is shown). */
  helper?: ReactNode;
  /** Error state — red border + (with `errorMessage`) an inline message. */
  error?: boolean;
  errorMessage?: string;
  /** Leading adornment (icon) inside the field. */
  leading?: ReactNode;
  /** Trailing adornment (icon / unit). */
  trailing?: ReactNode;
  /** Wrapper className (the field column). */
  containerClassName?: string;
}

export const Field = forwardRef<HTMLInputElement, FieldProps>(function Field(
  {
    label,
    helper,
    error = false,
    errorMessage,
    leading,
    trailing,
    className,
    containerClassName,
    id,
    testId,
    ...props
  },
  ref,
) {
  const reactId = useId();
  const inputId = id ?? reactId;
  const errorId = `${inputId}-error`;
  const helperId = `${inputId}-helper`;
  const hasErrorMessage = error && !!errorMessage;
  const describedBy = hasErrorMessage ? errorId : helper != null ? helperId : undefined;

  return (
    <div className={cn('flex flex-col items-stretch', containerClassName)}>
      {label != null && (
        <label
          htmlFor={inputId}
          className="mb-6 font-sans text-s13 font-w500 tracking-givmo text-text-secondary"
        >
          {label}
        </label>
      )}
      <div className="relative">
        {leading != null && (
          <span className="pointer-events-none absolute inset-y-0 left-10 flex items-center text-icon-muted">
            {leading}
          </span>
        )}
        <input
          ref={ref}
          id={inputId}
          aria-invalid={error || undefined}
          aria-describedby={describedBy}
          className={cn(
            'h-36 w-full rounded-md border bg-surface px-12',
            'font-sans text-s14 font-w400 tracking-givmo text-text placeholder:text-text-placeholder',
            'outline-none transition-[box-shadow,border-color]',
            // The input is white-on-white against a card, so its border is the only thing that
            // identifies the control — a control boundary needing ≥3:1 (WCAG 1.4.11). neutral-500 =
            // 4.68:1 on white / 4.47:1 on surface-sunken; hover darkens to neutral-600 (7.54:1)
            // rather than lightening to neutral-400 (2.52:1). Same pair as Checkbox/SearchInput.
            'border-neutral-500 hover:border-neutral-600',
            'focus-visible:border-brand-500 focus-visible:shadow-ring',
            'disabled:cursor-not-allowed disabled:bg-neutral-50 disabled:text-text-tertiary',
            error &&
              'border-danger-500 hover:border-danger-500 focus-visible:border-danger-500 focus-visible:shadow-ring-danger',
            leading != null && 'pl-32',
            trailing != null && 'pr-32',
            className,
          )}
          {...testIdProp(testId)}
          {...props}
        />
        {trailing != null && (
          <span className="absolute inset-y-0 right-10 flex items-center text-icon-muted">
            {trailing}
          </span>
        )}
      </div>
      {hasErrorMessage ? (
        <p
          id={errorId}
          className="mt-6 font-sans text-s12 font-w400 tracking-givmo text-danger-600"
        >
          {errorMessage}
        </p>
      ) : helper != null ? (
        <p
          id={helperId}
          className="mt-6 font-sans text-s12 font-w400 tracking-givmo text-text-tertiary"
        >
          {helper}
        </p>
      ) : null}
    </div>
  );
});
